class IicaseUpsellPopup extends HTMLElement {
  context = null;
  #phase = '';
  #busy = false;
  #uncertain = false;
  #count = 0;
  #loading = false;
  #loadPromise = null;
  #loadController = null;
  #contentReady = true;

  get dialog() { return this.querySelector('dialog'); }

  connectedCallback() {
    this.addEventListener('click', this.#click);
    this.dialog?.addEventListener('cancel', this.#cancel);
    this.dialog?.addEventListener('close', this.#closed);
    document.addEventListener('shopify:cart:lines-update', this.#cartUpdated);
  }

  disconnectedCallback() {
    this.#loadController?.abort();
    this.removeEventListener('click', this.#click);
    this.dialog?.removeEventListener('cancel', this.#cancel);
    this.dialog?.removeEventListener('close', this.#closed);
    document.removeEventListener('shopify:cart:lines-update', this.#cartUpdated);
    if (this.context?.form) delete this.context.form.dataset.iicaseUpsellPopupFlow;
    this.dialog?.close();
  }

  prepare(form, formData, root) {
    if (this.#busy) return null;
    this.#loadController?.abort();
    this.#loadController = null;
    this.#loadPromise = null;
    this.#loading = false;
    this.context = {
      form, root,
      variantId: String(formData.get('id') || ''),
      quantity: Number(formData.get('quantity')) || Number(form.dataset.quantityDefault) || 1,
      groupId: `iicase-${crypto.randomUUID()}`,
    };
    this.#phase = 'main';
    this.#busy = true;
    this.#uncertain = false;
    form.dataset.iicaseUpsellPopupFlow = 'true';
    this.#count = 0;
    this.#loadContent(this.context);
    // Main-product preorder properties are still merged by the original product form.
    return {
      mainProperties: {
        _iicase_upsell_group: this.context.groupId,
        _iicase_upsell_offer: root.getAttribute('data-iicase-upsell-offer-id') || '',
        _iicase_upsell_role: 'main',
        _iicase_upsell_discount_percent: '0',
      },
      items: [],
    };
  }

  updateSelection(count, total) {
    this.#count = count;
    const totalLabel = this.querySelector('[data-upsell-total]');
    if (totalLabel && total != null) totalLabel.textContent = total;
    this.#updateButton();
  }

  #updateButton() {
    this.querySelectorAll('[data-iicase-upsell-item-action]').forEach((action) => {
      const item = action.closest('[data-iicase-upsell-item]');
      const checkbox = item.querySelector('[data-iicase-upsell-toggle]');
      const selected = Boolean(checkbox?.checked);
      action.textContent = selected ? 'Remove' : 'Add';
      action.setAttribute('aria-pressed', String(selected));
      action.setAttribute('aria-label', `${selected ? 'Remove' : 'Add'} ${action.dataset.productTitle}`);
      action.disabled = this.#busy || this.#loading || !this.#contentReady || this.#uncertain || !checkbox || checkbox.disabled;
    });
    this.querySelector('.iicase-upsell-dialog__body').inert = this.#busy || this.#loading || !this.#contentReady || this.#uncertain;
    const button = this.querySelector('[data-upsell-add]');
    if (!button) return;
    button.disabled = this.#busy || this.#loading || !this.#contentReady || this.#uncertain || !this.context || this.#count <= 0;
    button.setAttribute('aria-busy', String(this.#busy));
    const label = this.querySelector('[data-upsell-add-label]');
    label.textContent = this.#loading ? 'Loading...' : this.#busy ? 'Adding...' : label.dataset.label || 'Add selected';
  }

  #loadContent(context) {
    if (!this.dataset.sectionId || !this.dataset.productUrl) return Promise.resolve(true);
    if (this.#loadPromise) return this.#loadPromise;
    this.#loading = true;
    this.#contentReady = false;
    this.#status('Loading accessories...');
    this.querySelector('[data-upsell-retry]').hidden = true;
    context.root.hidden = true;
    const controller = new AbortController();
    this.#loadController = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    this.#loadPromise = (async () => {
      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('variant', context.variantId);
        const response = await fetch(url.toString(), { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = [...html.querySelectorAll('[data-iicase-upsell-offer]')]
          .find(candidate => candidate.getAttribute('data-iicase-upsell-block-id') === this.dataset.blockId);
        if (!root || root.hasAttribute('data-iicase-upsell-deferred') || !root.querySelector('[data-iicase-upsell-main-product]')) {
          throw new Error('Accessories unavailable');
        }
        if (!this.isConnected || this.context !== context) return false;
        context.root.replaceWith(root);
        context.root = root;
        this.#contentReady = true;
        this.#status('');
        this.dispatchEvent(new CustomEvent('iicase:upsell-popup-refresh', { bubbles: true }));
        if (![...root.querySelectorAll('[data-iicase-upsell-item]')].some(item => !item.hidden && !item.classList.contains('is-unavailable'))) {
          this.#status('No accessories are available for this selection. You can continue to your cart.');
        }
        return true;
      } catch (_) {
        if (this.isConnected && this.context === context && this.#loadController === controller) {
          this.#status('Accessories could not be loaded. Retry or continue to your cart.');
          this.querySelector('[data-upsell-retry]').hidden = false;
        }
        return false;
      } finally {
        clearTimeout(timeout);
        if (this.#loadController === controller) {
          this.#loading = false;
          this.#loadPromise = null;
          this.#loadController = null;
          if (this.isConnected) this.#updateButton();
        }
      }
    })();
    this.#updateButton();
    return this.#loadPromise;
  }

  #status(message) {
    const status = this.querySelector('[data-upsell-status]');
    status.textContent = message;
    status.hidden = !message;
  }

  #cancel = (event) => {
    if (this.#busy) event.preventDefault();
  };

  #closed = () => {
    this.#loadController?.abort();
    this.#loadController = null;
    this.#loadPromise = null;
    this.#loading = false;
    if (this.context?.form) delete this.context.form.dataset.iicaseUpsellPopupFlow;
    this.context = null;
    this.#phase = '';
    this.dispatchEvent(new CustomEvent('iicase:upsell-popup-refresh', { bubbles: true }));
  };

  #click = async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-upsell-preview]')) {
      this.#status('Preview only. Add a product on the storefront to test purchasing.');
      this.dialog.showModal();
    } else if (event.target.closest('[data-upsell-close]') || event.target === this.dialog) {
      if (!this.#busy) this.dialog.close();
    } else if (event.target.closest('[data-upsell-cart]')) {
      event.preventDefault();
      if (this.#busy) return;
      this.#busy = true;
      this.#updateButton();
      try {
        await this.#showCartDrawer();
      } finally {
        this.#busy = false;
        this.#updateButton();
      }
    } else if (event.target.closest('[data-upsell-add]')) {
      this.#submit();
    } else if (event.target.closest('[data-upsell-retry]') && this.context && !this.#busy) {
      this.#loadContent(this.context);
    }
  };

  async #submit() {
    if (this.#busy || this.#loading || !this.#contentReady || this.#uncertain || !this.context || !this.#count) return;
    this.#busy = true;
    this.#updateButton();
    this.#status('');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to check your cart. Please try again.');
      const cart = await response.json();
      const main = cart.items.find((item) => item.properties?._iicase_upsell_group === this.context.groupId &&
        item.properties?._iicase_upsell_role === 'main');
      if (!main) throw new Error('The original item is no longer in your cart. Please add it again.');
      const payload = await window.iicaseBuildUpsellPopupPayload(this);
      if (!payload?.items?.length) throw new Error('Please choose an available accessory.');
      this.#phase = 'addons';
      this.context.form.dataset.iicaseUpsellPopupFlow = 'true';
      // Only accessories are submitted. Never resubmit the main variant or its preorder properties.
      this.context.form.addUpsellItems(payload.items, { discountCode: payload.discountCode });
    } catch (error) {
      this.#phase = '';
      if (this.context?.form) delete this.context.form.dataset.iicaseUpsellPopupFlow;
      this.#status(error.message || 'Unable to add accessories. Please try again.');
      this.#busy = false;
      this.#updateButton();
    }
  }

  async #showCartDrawer() {
    try {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') throw new Error('Cart drawer unavailable');
      await drawer.querySelector('cart-items-component')?.cartUpdateComplete;
      if (!this.isConnected) return;
      // Native close restores focus before the drawer captures its return target.
      if (this.dialog.open) {
        await new Promise((resolve) => {
          this.dialog.addEventListener('close', resolve, { once: true });
          this.dialog.close();
        });
      }
      drawer.open();
    } catch (_) {
      // Adding succeeded; a missing or failed drawer must not invite a duplicate submission.
      this.dialog.close();
      window.location.assign(this.dataset.cartUrl);
    }
  }

  #cartUpdated = async (event) => {
    const context = this.context;
    if (!context || event.target !== context.form || event.action !== 'add' || !event.promise || !this.#phase) return;
    const phase = this.#phase;
    this.#phase = '';
    try {
      const result = await event.promise;
      if (!this.isConnected || this.context !== context) return;
      if (result?.detail?.didError) {
        if (phase === 'addons') this.#status('Accessories could not be added. Check availability and try again.');
        else {
          this.context = null;
          this.#loadController?.abort();
        }
        return;
      }
      if (phase === 'main') {
        if (!this.dataset.sectionId) this.#status('');
        this.dispatchEvent(new CustomEvent('iicase:upsell-popup-refresh', { bubbles: true }));
        const items = [...context.root.querySelectorAll('[data-iicase-upsell-item]')];
        if (!this.dataset.sectionId && !items.some((item) => !item.hidden && !item.classList.contains('is-unavailable'))) return;
        this.dialog.showModal();
        const scroll = this.querySelector('.iicase-upsell-dialog__items-scroll');
        if (scroll) scroll.scrollTop = 0;
        this.querySelector('[data-upsell-close]').focus({ preventScroll: true });
      } else {
        this.#status('Added to your cart.');
        await this.#showCartDrawer();
      }
    } catch (_) {
      // A failed confirmation may follow a successful write. Do not offer a blind duplicate retry.
      if (phase === 'addons') {
        this.#uncertain = true;
        this.#status('We could not confirm the update. Please view your cart before trying again.');
      }
    } finally {
      delete context.form.dataset.iicaseUpsellPopupFlow;
      this.#busy = false;
      this.#updateButton();
    }
  };
}

if (!customElements.get('iicase-upsell-popup')) customElements.define('iicase-upsell-popup', IicaseUpsellPopup);
